import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Bell, Check, Package, X, ArrowLeftRight, Truck, FileText, AlertTriangle, Filter, ClipboardList, MessageSquare } from 'lucide-react';
import { getUserNotifications, getUnreadNotificationCount, markNotificationRead, markAllNotificationsRead } from '@/lib/api';

const getConfig = (type, t) => {
  const configs = {
    match_new_surplus: {
      icon: ArrowLeftRight,
      color: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-100 dark:bg-emerald-900/30',
      unreadBg: 'bg-emerald-50/80 dark:bg-emerald-900/15',
      label: t('notifications.typeMatch'),
      dot: 'bg-emerald-500',
    },
    material_match: {
      icon: Package,
      color: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      unreadBg: 'bg-blue-50/80 dark:bg-blue-900/15',
      label: t('notifications.typeApproval'),
      dot: 'bg-blue-500',
    },
    new_request: {
      icon: FileText,
      color: 'text-orange-600 dark:text-orange-400',
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      unreadBg: 'bg-orange-50/80 dark:bg-orange-900/15',
      label: t('notifications.typeRequest'),
      dot: 'bg-orange-500',
    },
    escalation: {
      icon: AlertTriangle,
      color: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-100 dark:bg-red-900/30',
      unreadBg: 'bg-red-50/80 dark:bg-red-900/15',
      label: t('notifications.typeEscalation'),
      dot: 'bg-red-500',
    },
    logistics: {
      icon: Truck,
      color: 'text-violet-600 dark:text-violet-400',
      bg: 'bg-violet-100 dark:bg-violet-900/30',
      unreadBg: 'bg-violet-50/80 dark:bg-violet-900/15',
      label: t('notifications.typeLogistics'),
      dot: 'bg-violet-500',
    },
    wanted_ad_match: {
      icon: ClipboardList,
      color: 'text-purple-600 dark:text-purple-400',
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      unreadBg: 'bg-purple-50/80 dark:bg-purple-900/15',
      label: 'Gesuch-Match',
      dot: 'bg-purple-500',
    },
    new_message: {
      icon: MessageSquare,
      color: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      unreadBg: 'bg-blue-50/80 dark:bg-blue-900/15',
      label: 'Nachricht',
      dot: 'bg-blue-500',
    },
  };

  return configs[type] || {
    icon: Bell,
    color: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-100 dark:bg-slate-800',
    unreadBg: 'bg-orange-50/50 dark:bg-orange-900/10',
    label: t('notifications.typeInfo'),
    dot: 'bg-slate-500',
  };
};

const isMatchType = (type) => type === 'match_new_surplus' || type === 'material_match';

export const NotificationBell = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const dropdownRef = useRef(null);

  const timeAgo = useCallback((dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('notifications.justNow');
    if (mins < 60) return t('notifications.minutesAgo', { count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('notifications.hoursAgo', { count: hrs });
    const days = Math.floor(hrs / 24);
    if (days < 7) return t('notifications.daysAgo', { count: days });
    return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  }, [t]);

  const FILTERS = [
    { key: 'all', label: t('notifications.filterAll') },
    { key: 'match', label: t('notifications.filterMatches') },
    { key: 'other', label: t('notifications.filterOther') },
  ];

  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await getUnreadNotificationCount();
      setUnreadCount(res.data.count);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      setLoading(true);
      try {
        const res = await getUserNotifications(30);
        setNotifications(res.data);
      } catch { /* silent */ }
      setLoading(false);
    }
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      try {
        await markNotificationRead(notif.notification_id);
        setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch { /* silent */ }
    }
    if (notif.data?.material_id) {
      navigate(`/material/${notif.data.material_id}`);
      setOpen(false);
    } else if (notif.type === 'new_message' && notif.data?.thread_id) {
      navigate(`/messages?thread=${notif.data.thread_id}`);
      setOpen(false);
    } else if (notif.type === 'wanted_ad_match') {
      navigate('/wanted');
      setOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const goToMatchingDashboard = () => {
    navigate('/matching');
    setOpen(false);
  };

  const filtered = notifications.filter((n) => {
    if (filter === 'match') return isMatchType(n.type);
    if (filter === 'other') return !isMatchType(n.type);
    return true;
  });

  const matchCount = notifications.filter(n => isMatchType(n.type) && !n.read).length;

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleDropdown}
        className="relative"
        data-testid="notification-bell"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-in fade-in"
            data-testid="notification-badge-count"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[340px] sm:w-[400px] bg-background border rounded-xl shadow-2xl z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50/80 dark:bg-slate-800/80">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-orange-500" />
              <span className="font-semibold text-sm">{t('notifications.title')}</span>
              {unreadCount > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-orange-600 hover:text-orange-700"
                  onClick={handleMarkAllRead}
                  data-testid="mark-all-read-btn"
                >
                  <Check className="h-3 w-3 mr-1" /> {t('notifications.allRead')}
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Match banner */}
          {matchCount > 0 && (
            <button
              onClick={goToMatchingDashboard}
              className="w-full flex items-center gap-2 px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-100 dark:border-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
              data-testid="match-banner"
            >
              <ArrowLeftRight className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                {matchCount === 1
                  ? t('notifications.newMatch', { count: matchCount })
                  : t('notifications.newMatches', { count: matchCount })}
              </span>
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 ml-auto">
                {t('notifications.goToDashboard')} &rarr;
              </span>
            </button>
          )}

          {/* Filter tabs */}
          <div className="flex items-center gap-1 px-3 py-2 border-b bg-background">
            <Filter className="h-3 w-3 text-muted-foreground mr-1" />
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                  filter === f.key
                    ? 'bg-orange-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-muted-foreground hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
                data-testid={`notification-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Notification list */}
          <div className="max-h-[360px] overflow-y-auto" data-testid="notification-list">
            {loading ? (
              <div className="py-10 text-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500 mx-auto" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">
                {filter === 'all' ? t('notifications.none') : t('notifications.noFiltered')}
              </div>
            ) : (
              filtered.map((notif) => {
                const cfg = getConfig(notif.type, t);
                const Icon = cfg.icon;
                return (
                  <button
                    key={notif.notification_id}
                    className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
                      !notif.read ? cfg.unreadBg : ''
                    }`}
                    onClick={() => handleClick(notif)}
                    data-testid={`notification-item-${notif.notification_id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`shrink-0 mt-0.5 p-1.5 rounded-lg ${!notif.read ? cfg.bg : 'bg-slate-100 dark:bg-slate-800'}`}>
                        <Icon className={`h-3.5 w-3.5 ${!notif.read ? cfg.color : 'text-muted-foreground'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-sm leading-tight ${!notif.read ? 'font-semibold' : 'font-medium text-muted-foreground'}`}>
                            {notif.title}
                          </p>
                          <span className={`shrink-0 text-[9px] px-1.5 py-0.5 rounded-full font-medium ${!notif.read ? cfg.bg + ' ' + cfg.color : 'bg-slate-100 dark:bg-slate-800 text-muted-foreground'}`}>
                            {cfg.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">{notif.message}</p>
                        {isMatchType(notif.type) && notif.data?.offer_site_name && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <Package className="h-3 w-3 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground">
                              {t('notifications.site')}: {notif.data.offer_site_name}
                            </span>
                          </div>
                        )}
                        <p className="text-[10px] text-muted-foreground/70 mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                      {!notif.read && (
                        <div className="shrink-0 mt-2">
                          <div className={`h-2 w-2 rounded-full ${cfg.dot}`} />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t px-4 py-2 bg-slate-50/80 dark:bg-slate-800/80">
              <button
                onClick={goToMatchingDashboard}
                className="w-full text-center text-xs text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium py-1 transition-colors"
                data-testid="go-to-matching-btn"
              >
                {t('notifications.matchDashboard')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
